import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, DatabaseZap, CloudOff } from 'lucide-react';
import { usePatients } from '../contexts/AppContext';
import { formatCacheAge } from '../services/patientCache';
import { getQueueSize } from '../services/syncQueue';
import { isOnline as getIsOnline } from '../utils/connectivity';

const OfflineBanner: React.FC = () => {
  // Use the probed state rather than navigator.onLine — avoids false-positive
  // on captive portals (HMIS login walls that block Supabase)
  const [isOnline, setIsOnline] = useState(getIsOnline);
  const [showReconnected, setShowReconnected] = useState(false);
  const [pendingCount, setPendingCount] = useState(() => getQueueSize());
  const { isStale, cacheTimestamp } = usePatients();

  // Poll queue size every 5s so the count stays current
  useEffect(() => {
    const id = setInterval(() => setPendingCount(getQueueSize()), 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 3500);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ─── Reconnected flash ───
  if (showReconnected) {
    return (
      <div role="status" aria-live="polite" className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 py-2 px-4 text-sm font-semibold shadow-lg bg-emerald-500 text-white transition-all duration-300">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Back online — syncing{pendingCount > 0 ? ` ${pendingCount} offline change${pendingCount > 1 ? 's' : ''}` : ' offline changes'}…
      </div>
    );
  }

  // ─── No connection ───
  if (!isOnline) {
    return (
      <div role="alert" aria-live="assertive" className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 py-2 px-4 text-sm font-semibold shadow-lg bg-amber-500 text-white transition-all duration-300">
        <WifiOff className="w-4 h-4" />
        You&apos;re offline — showing cached data
        {cacheTimestamp && (
          <span className="opacity-75 text-xs font-normal ml-1">
            · last synced {formatCacheAge(cacheTimestamp)}
          </span>
        )}
        {pendingCount > 0 && (
          <span className="flex items-center gap-1 ml-2 bg-amber-700/50 rounded-full px-2 py-0.5 text-xs font-bold">
            <CloudOff className="w-3 h-3" /> {pendingCount} pending
          </span>
        )}
      </div>
    );
  }

  // ─── Online but serving stale cache (background fetch in progress) ───
  if (isStale && cacheTimestamp) {
    const age = formatCacheAge(cacheTimestamp);
    // Only show if cache is older than 2 minutes — avoids flash on fast connections
    const isOld = Date.now() - new Date(cacheTimestamp).getTime() > 2 * 60 * 1000;
    if (!isOld) return null;

    return (
      <div role="status" aria-live="polite" className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 py-1.5 px-4 text-xs font-medium shadow-md bg-teal-600 text-white transition-all duration-300">
        <DatabaseZap className="w-3.5 h-3.5 animate-pulse" />
        Showing cached data from {age} · Syncing in background…
      </div>
    );
  }

  return null;
};

export default OfflineBanner;
